import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/data";
import { LogoWordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#050B24]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <LogoWordmark />

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive
                    ? "text-red-500"
                    : "text-slate-300 hover:text-white"
                }`
              }
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:+91${BRAND.phone}`}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors whitespace-nowrap"
            data-testid="navbar-phone-link"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden xl:inline">{BRAND.phoneDisplay}</span>
          </a>
          <Link to="/contact">
            <Button
              className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full px-5 shadow-[0_8px_24px_rgba(220,38,38,0.35)]"
              data-testid="navbar-cta-enquire"
            >
              Get Consultation
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#050B24]/95 backdrop-blur-xl border-t border-white/10"
          data-testid="nav-mobile-panel"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? "text-red-500 bg-white/5"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-3"
              data-testid="nav-mobile-cta"
            >
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full">
                Get Consultation
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
